import styled, { css } from "styled-components";
import { colors, breakpoints } from "definitions";

/* ---------------------------------------------------------------------------
   Formulario de postulación
   ---------------------------------------------------------------------------
   Antes eran dos tarjetas separadas, cada una encabezada por una barra azul de
   52px con el título centrado en blanco, y adentro treinta y pico de campos
   seguidos con `padding: 35px` fijo en cualquier resolución. Ahora es una sola
   tarjeta con dos secciones numeradas, y los campos van agrupados por lo que
   son: proyecto, responsable, ubicación y redes.

   Las clases `form-section` y `form-section-header` las usan las reglas
   @media print de `src/index.js`; `form-body` y `form-group` reemplazan a los
   selectores que ahí colgaban del `style="padding: 35px"`.
--------------------------------------------------------------------------- */

export const EntryForm = styled.form`
  display: contents;
`;

export const FormCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid #dde3ee;
  border-radius: 14px;
  background: ${colors.background.white};
  box-shadow: 0 2px 10px rgba(8, 37, 90, 0.06);
  overflow: hidden;

  /* En la vista de lectura el asterisco de obligatorio no dice nada: ya no hay
     nada que completar. */
  ${({ $readOnly }) =>
    $readOnly &&
    css`
      label:after,
      h3 span:after {
        content: none;
      }
    `};

  /* Dentro del modal el marco lo pone el modal: la tarjeta encima quedaba
     como un recuadro dentro de otro. */
  ${({ $flat }) =>
    $flat &&
    css`
      border: none;
      border-radius: 0;
      box-shadow: none;
    `};
`;

export const Section = styled.section.attrs({ className: "form-section" })`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 2.6rem 2.8rem 3rem;

  + section {
    border-top: 1px solid #eef2f8;
  }

  /* Cada campo separado del de arriba y del de abajo: los Row de
     react-styled-flexboxgrid no traen separación vertical, así que sin esto
     el rótulo de un campo queda pegado al input del anterior. */
  .field-input-group {
    margin-bottom: 2rem;
  }

  ${breakpoints.tablet} {
    padding: 2rem 1.6rem 2.4rem;
  }
`;

export const Header = styled.div.attrs({ className: "form-section-header" })`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.2rem 1.4rem;
  align-items: center;
  width: 100%;
  padding-bottom: 1.4rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid #eef2f8;
`;

export const SectionNumber = styled.span`
  grid-row: span 2;
  display: grid;
  place-items: center;
  width: 3rem;
  height: 3rem;
  border-radius: 8px;
  background: ${colors.background.yellowGreen};
  color: ${colors.fonts.white};
  font-family: Lato;
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1;
`;

export const Title = styled.h3`
  margin: 0;
  font-family: Lato;
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 2.4rem;
  color: ${colors.icons.darkBlue};
`;

export const SectionHint = styled.p`
  margin: 0;
  font-family: Lato;
  font-size: 1.3rem;
  font-weight: 400;
  line-height: 1.8rem;
  color: #5b6470;
`;

// `attrs` como función para no pisar el className que le pase quien lo use
// (por ejemplo `print-hide` en el bloque del postulante).
export const Group = styled.div.attrs((props) => ({
  className: ["form-group", props.className].filter(Boolean).join(" "),
}))`
  display: flex;
  flex-direction: column;
  width: 100%;

  + div {
    margin-top: 1.6rem;
  }
`;

export const GroupTitle = styled.h4`
  margin: 0 0 1rem;
  font-family: Lato;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.12rem;
  line-height: 1.6rem;
  text-transform: uppercase;
  color: #7b889f;

  small {
    font-size: inherit;
    font-weight: 400;
    letter-spacing: 0;
    text-transform: none;
  }
`;

// El avatar deja de ser un círculo de 300px que empujaba todo el formulario a
// la derecha: entra en el grupo "Proyecto", al lado de sus campos.
export const ProjectRow = styled.div`
  display: grid;
  /* Sin avatar (vista de lectura sin logo) es una sola columna: con dos, la
     columna vacía se quedaba con el ancho y los campos entraban apretados. */
  grid-template-columns: ${({ $withAvatar }) =>
    $withAvatar === false ? "minmax(0, 1fr)" : "auto minmax(0, 1fr)"};
  gap: 1.6rem 2.4rem;
  align-items: start;
  width: 100%;

  ${breakpoints.tablet} {
    grid-template-columns: minmax(0, 1fr);
    justify-items: center;
  }
`;

export const ProjectFields = styled.div`
  min-width: 0;
`;

export const Divider = styled.div`
  height: 1px;
  width: 100%;
  margin: 2.2rem 0 0;
  background: #eef2f8;
`;

export const FooterCol = styled.div`
  flex-basis: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  ${breakpoints.tablet} {
    flex-direction: column;
    align-items: center;

    button {
      width: 100%;
    }
  }
`;

export const LinkSocialNetwork = styled.a`
  text-decoration: underline;
  font-weight: 300;
  border: none;
  color: black;
  box-sizing: border-box;
  border-radius: 4px;
  height: 3.8rem;
  padding: 0.8rem;
  width: 100%;
`;

export const SocialNetworksWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
